import { useCallback, useEffect, useState } from 'react';

import {
  getPayoutAccount, startPayoutOnboarding, refreshPayoutAccount,
  type OrganizerPayoutAccount,
} from '../../lib/organizer';
import { ApiError } from '../../lib/apiClient';
import './OrganizerPages.css';

/**
 * Settlement and payouts for an organizer.
 *
 * WHAT THIS SCREEN IS FOR. Net proceeds on an event with no investment raise now transfer
 * to the organizer's own Stripe Connect account. Before that existed, those proceeds sat
 * in Voleto's platform balance and were settled some other way, with nothing on the
 * platform recording that the organizer was owed them. This is where an organizer sets
 * that account up and sees whether it can actually receive money.
 *
 * WHY IT IS SELF-SERVE, unlike an SPV's account or a venue's, which an admin configures:
 * the bank account being verified belongs to the organizer, so the identity documents and
 * bank details are theirs to supply. An admin cannot and should not be able to.
 *
 * THE STATUS IS NOT THE GATE. An account can read "complete" and still have payouts
 * disabled because a verification lapsed, so the screen keys everything off
 * canReceiveTransfers, which is what the engine's transfer gate actually asks. Showing
 * "complete" beside a payout that will not move is worse than showing nothing.
 *
 * WHAT HAPPENS IF THIS IS NEVER SET UP. Settlement still runs and still records what is
 * owed; the transfer defers and stays retryable. The copy says that rather than implying
 * the money is lost or that settlement is blocked.
 */

const WATERFALL: [string, string][] = [
  ['Gross ticket revenue', 'Everything buyers paid, less anything refunded.'],
  ['Card processing', '2.9% plus 30 cents per order, charged by Stripe.'],
  ['Voleto platform fee', '5% of gross.'],
  ['Venue revenue share', 'Only where the venue negotiated one, at the rate your event was published under.'],
  ['Net proceeds to you', 'Transferred to your account after the event settles.'],
];

export function OrganizerPayouts() {
  const [account, setAccount] = useState<OrganizerPayoutAccount | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    getPayoutAccount()
      .then(setAccount)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not load your payout account.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function onboard() {
    setBusy(true);
    setError(null);
    try {
      const { url } = await startPayoutOnboarding(window.location.origin);
      window.location.assign(url);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not start onboarding.');
      setBusy(false);
    }
  }

  async function refresh() {
    setBusy(true);
    setError(null);
    try {
      setAccount(await refreshPayoutAccount());
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not refresh your account.');
    } finally {
      setBusy(false);
    }
  }

  const started = Boolean(account?.stripeConnectAccountId);
  const ready = account?.canReceiveTransfers === true;

  return (
    <div className="org-page">
      <header className="org-header">
        <div>
          <h1 className="org-title">Settlement and payouts</h1>
          <p className="org-lede">
            After an event settles, your net proceeds transfer to your own account. Events
            backed by an investment raise pay their production entity instead.
          </p>
        </div>
      </header>

      {error && <p className="org-status org-error" role="alert">{error}</p>}
      {loading && <p className="org-status">Loading…</p>}

      {!loading && account && (
        <section className="org-card glass-panel">
          <header className="org-card__head">
            <h2 className="org-card__title">Your payout account</h2>
            <span className={`org-pill org-pill--${ready ? 'approved' : 'pending'}`}>
              {ready ? 'Ready to receive' : started ? 'Verification pending' : 'Not set up'}
            </span>
          </header>

          <div className="org-card__body">
            {!started && (
              <p className="org-copy">
                You have not set up payouts yet. Your events still settle and Voleto still
                records exactly what you are owed, the transfer simply waits until this is
                finished. Stripe handles the identity and bank verification.
              </p>
            )}

            {started && !ready && (
              <>
                <p className="org-copy">
                  Stripe has not finished verifying this account, so transfers will be
                  recorded as owed and held until it does. Nothing is lost while you wait.
                </p>
                {account.stripeDisabledReason && (
                  <p className="org-copy org-muted mono">{account.stripeDisabledReason}</p>
                )}
                {account.requirementsDue.length > 0 && (
                  <>
                    <p className="org-copy">Stripe still needs:</p>
                    <ul className="org-list">
                      {account.requirementsDue.map((r) => (
                        <li key={r} className="mono">{r}</li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}

            {ready && (
              <p className="org-copy">
                This account can receive transfers. Net proceeds are sent automatically
                once each event settles.
              </p>
            )}

            <div className="org-card__actions">
              {!ready && (
                <button type="button" className="button button--primary"
                  disabled={busy} onClick={onboard}>
                  {busy ? 'Opening…' : started ? 'Continue setup' : 'Set up payouts'}
                </button>
              )}
              {started && (
                <button type="button" className="button button--ghost"
                  disabled={busy} onClick={refresh}>
                  {busy ? 'Checking…' : 'Refresh status'}
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* The deductions, stated once and in the order they are taken. An organizer reading
          a settlement figure is owed an explanation of how it was reached. */}
      <section className="org-card glass-panel">
        <header className="org-card__head">
          <h2 className="org-card__title">How your proceeds are calculated</h2>
        </header>
        <div className="org-card__body">
          <dl className="org-metrics">
            {WATERFALL.map(([label, detail]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{detail}</dd>
              </div>
            ))}
          </dl>
          <p className="org-copy org-muted">
            Your own production costs are not deducted here. You receive the full net
            proceeds and pay your costs from that, the same way a production entity does.
          </p>
        </div>
      </section>
    </div>
  );
}
